import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useMemoizedFn } from 'ahooks';
import { matchPath, useLocation } from 'react-router-dom';
import { PagePath } from '@/routes/paths';
import useStore from '@/stores/sider-menu';
import useRequestsStore from '@/stores/request-session';
import VirtualList, { VirtualListInstance } from '@/common/base/virtual-scroll-list';
import SiderMenuEvent from '@/events/sider-menu';
import useUserStore from '@/stores/userStore';
import RequestListItem from './list-item';

export default function RequestList() {
  const { requestListExpanded, setRequestListExpanded } = useStore();
  const { scrollOffset, setScrollOffset, requestSessionList, loadSessionList, reloadSessionList } = useRequestsStore();
  const location = useLocation();
  const matchRouter = matchPath(PagePath.chat, location.pathname);
  const vitualListRef = useRef<VirtualListInstance>(null);
  const { user } = useUserStore();

  const reloadData = useMemoizedFn(() => {
    return reloadSessionList();
  });

  const scrollToCurrent = useMemoizedFn(() => {
    if (!matchRouter?.params?.sessionId) {
      // 路由不匹配 忽略
      return;
    }

    if (vitualListRef.current) {
      const index = requestSessionList.findIndex(item => item.sessionId === matchRouter.params.sessionId);
      if (index > -1) {
        vitualListRef.current?.scrollToIndex(index, { align: 'center', behavior: 'smooth' });
        return;
      }
    }
  });

  useEffect(() => {
    if (user?.userId) {
      reloadData().then(() => {
        scrollToCurrent();
      });
    }
  }, [reloadData, scrollToCurrent, user?.userId]);

  /**
   * 监听全局自动聚焦当前请求事件
   */
  useEffect(() => {
    const handleAutoFocus = () => {
      scrollToCurrent();
    };

    SiderMenuEvent.on('autoFocusCurrentRequest', handleAutoFocus);
    return () => {
      SiderMenuEvent.off('autoFocusCurrentRequest', handleAutoFocus);
    };
  }, [scrollToCurrent]);

  useEffect(() => {
    if (matchRouter?.params?.sessionId) {
      scrollToCurrent();
      setRequestListExpanded(true);
    }
  }, [matchRouter?.params?.sessionId, scrollToCurrent, setRequestListExpanded]);

  if (!requestListExpanded) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.12, ease: 'easeOut' }}
    >
      <div className="border-l border-[#E5E4E4] pl-2 ml-7 pr-2">
        <VirtualList
          ref={vitualListRef}
          data={requestSessionList}
          initialOffset={scrollOffset}
          setScrollOffset={setScrollOffset}
          getScrollElement={() => document.querySelector('#request_list_scroller') as HTMLElement}
          estimateSize={() => 38}
          renderItem={(item, index) => <RequestListItem key={item.sessionId} data={item} index={index} />}
          onBottomReached={loadSessionList}
        />
      </div>
    </motion.div>
  );
}
